import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { usersAPI, companiesAPI, sedesAPI, teamsAPI, areasAPI, cargosAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Users, Plus, Trash2, Edit, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Toaster, toast } from 'sonner';

const ROLES = [
  { value: 'colaborador', label: 'Colaborador' },
  { value: 'admin', label: 'Administrador' },
  { value: 'superadmin', label: 'Super Administrador' },
];

const UsersPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [areas, setAreas] = useState([]);
  const [teams, setTeams] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '', password: '', name: '', role: 'colaborador',
    company_id: 'none', sede_id: 'none', team_id: 'none', area_id: 'none', cargo_id: 'none', priority_role: ''
  });
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, companiesRes, sedesRes, areasRes, teamsRes, cargosRes] = await Promise.all([
        usersAPI.getAll(),
        currentUser?.role === 'superadmin' ? companiesAPI.getAll() : Promise.resolve({ data: [] }),
        sedesAPI.getAll(),
        areasAPI.getAll(),
        teamsAPI.getAll(),
        cargosAPI.getAll(),
      ]);
      setUsers(usersRes.data);
      setCompanies(companiesRes.data);
      setSedes(sedesRes.data);
      setAreas(areasRes.data);
      setTeams(teamsRes.data);
      setCargos(cargosRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Convert "none" values to empty strings for backend
      const dataToSend = { ...formData };
      ['company_id', 'sede_id', 'team_id', 'area_id', 'cargo_id'].forEach(key => {
        if (dataToSend[key] === 'none') dataToSend[key] = '';
      });
      
      if (editingUser) {
        const { email, password, ...updateData } = dataToSend;
        await usersAPI.update(editingUser.id, updateData);
        toast.success('Usuario actualizado');
      } else {
        await usersAPI.create(dataToSend);
        toast.success('Usuario creado exitosamente');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar usuario');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '', password: '', name: '', role: 'colaborador',
      company_id: '', sede_id: '', team_id: '', area_id: '', cargo_id: '', priority_role: ''
    });
    setEditingUser(null);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      name: user.name,
      role: user.role,
      company_id: user.company_id || 'none',
      sede_id: user.sede_id || 'none',
      team_id: user.team_id || 'none',
      area_id: user.area_id || 'none',
      cargo_id: user.cargo_id || 'none',
      priority_role: user.priority_role || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este usuario?')) return;
    try {
      await usersAPI.delete(id);
      toast.success('Usuario eliminado');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar usuario');
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'superadmin': return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'admin': return 'bg-blue-50 text-blue-700 border-blue-200';
      default: return 'bg-slate-50 text-slate-700 border-slate-200';
    }
  };

  return (
    <AdminLayout>
      <Toaster position="top-right" />
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>
              Gestión de Usuarios
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Administra los usuarios del sistema
            </p>
          </div>
          <div className="flex items-center gap-2">
            {users.length > 0 && <span className="text-sm text-slate-500" data-testid="users-total-count">{users.length} usuarios</span>}
          <Button 
            onClick={() => { resetForm(); setDialogOpen(true); }} 
            className="bg-[#002FA7] hover:bg-[#001B66] rounded-sm"
            data-testid="add-user-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Usuario
          </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
          </div>
        ) : users.length === 0 ? (
          <Card className="border border-slate-200">
            <CardContent className="flex flex-col items-center justify-center h-64 text-center">
              <Users className="w-12 h-12 text-slate-300 mb-4" />
              <p className="text-slate-500">No hay usuarios registrados</p>
            </CardContent>
          </Card>
        ) : (
          <>
          <div className="overflow-x-auto">
            <table className="data-table" data-testid="users-table">
              <thead>
                <tr>
                  <th>Usuario</th>
                  <th>Email</th>
                  <th>Rol</th>
                  <th>Sede</th>
                  <th>Equipo</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {users.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#002FA7] flex items-center justify-center text-white text-sm font-medium">
                          {u.name?.charAt(0).toUpperCase()}
                        </div>
                        <span className="font-medium">{u.name}</span>
                      </div>
                    </td>
                    <td className="text-slate-500">{u.email}</td>
                    <td>
                      <span className={`inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium border ${getRoleBadgeColor(u.role)}`}>
                        {ROLES.find(r => r.value === u.role)?.label || u.role}
                      </span>
                    </td>
                    <td>{sedes.find(s => s.id === u.sede_id)?.name || '-'}</td>
                    <td>{teams.find(t => t.id === u.team_id)?.name || '-'}</td>
                    <td>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(u)} className="text-slate-400 hover:text-blue-500">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(u.id)} className="text-slate-400 hover:text-red-500" disabled={u.id === currentUser?.id}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {Math.ceil(users.length / PAGE_SIZE) > 1 && (
            <div className="flex items-center justify-between border-t border-slate-200 pt-4" data-testid="users-pagination">
              <p className="text-sm text-slate-500">
                {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, users.length)} de {users.length}
              </p>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" className="h-8 w-8 rounded-sm" disabled={page === 1} onClick={() => setPage(page - 1)} data-testid="users-prev-page">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                {Array.from({ length: Math.ceil(users.length / PAGE_SIZE) }, (_, i) => i + 1).map((p) => (
                  <Button
                    key={p}
                    variant={p === page ? 'default' : 'outline'}
                    size="icon"
                    className={`h-8 w-8 rounded-sm text-xs ${p === page ? 'bg-[#002FA7] hover:bg-[#001B66]' : ''}`}
                    onClick={() => setPage(p)}
                    data-testid={`users-page-${p}`}
                  >
                    {p}
                  </Button>
                ))}
                <Button variant="outline" size="icon" className="h-8 w-8 rounded-sm" disabled={page === Math.ceil(users.length / PAGE_SIZE)} onClick={() => setPage(page + 1)} data-testid="users-next-page">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
          </>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Cabinet Grotesk' }}>
                {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nombre completo</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="rounded-sm"
                    data-testid="user-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    disabled={!!editingUser}
                    className="rounded-sm"
                    data-testid="user-email-input"
                  />
                </div>
                {!editingUser && (
                  <div className="space-y-2">
                    <Label>Contraseña</Label>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required={!editingUser}
                      className="rounded-sm"
                      data-testid="user-password-input"
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Rol</Label>
                  <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                    <SelectTrigger className="rounded-sm" data-testid="user-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.filter(r => currentUser?.role === 'superadmin' || r.value !== 'superadmin').map((r) => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {currentUser?.role === 'superadmin' && companies.length > 0 && (
                  <div className="space-y-2">
                    <Label>Empresa</Label>
                    <Select value={formData.company_id} onValueChange={(value) => setFormData({ ...formData, company_id: value })}>
                      <SelectTrigger className="rounded-sm">
                        <SelectValue placeholder="Seleccionar empresa" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin empresa</SelectItem>
                        {companies.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Sede</Label>
                  <Select value={formData.sede_id} onValueChange={(value) => setFormData({ ...formData, sede_id: value })}>
                    <SelectTrigger className="rounded-sm">
                      <SelectValue placeholder="Seleccionar sede" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin sede</SelectItem>
                      {sedes.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Área</Label>
                  <Select value={formData.area_id} onValueChange={(value) => setFormData({ ...formData, area_id: value })}>
                    <SelectTrigger className="rounded-sm">
                      <SelectValue placeholder="Seleccionar área" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin área</SelectItem>
                      {areas.map((a) => (
                        <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Equipo</Label>
                  <Select value={formData.team_id} onValueChange={(value) => setFormData({ ...formData, team_id: value })}>
                    <SelectTrigger className="rounded-sm">
                      <SelectValue placeholder="Seleccionar equipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin equipo</SelectItem>
                      {teams.map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Cargo</Label>
                  <Select value={formData.cargo_id} onValueChange={(value) => setFormData({ ...formData, cargo_id: value })}>
                    <SelectTrigger className="rounded-sm">
                      <SelectValue placeholder="Seleccionar cargo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin cargo</SelectItem>
                      {cargos.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Rol de prioridad (opcional)</Label>
                  <Input
                    value={formData.priority_role}
                    onChange={(e) => setFormData({ ...formData, priority_role: e.target.value })}
                    placeholder="Ej: gerente, director"
                    className="rounded-sm"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">Cancelar</Button>
                <Button type="submit" className="bg-[#002FA7] hover:bg-[#001B66] rounded-sm" disabled={saving} data-testid="save-user-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Guardar
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default UsersPage;
